import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IADatabaseDocument, IADatabaseSearchAndFilters } from "@/src/types/admissionDatabase";
import {
  admissionDatabaseDefaultBodyFilters,
  admissionDatabaseDefaultConfiguration,
  admissionDatabaseAllFieldsList,
  normalizeVisibleAODColumns,
} from "@/src/constants/AdmissionOffice/admissionOfficeDatabaseConsts";
import { getUserConfigFromStorage } from "@/src/utils/userConfigStorage";
// import {
//   onlineApplicationsDefaultConfiguration,
//   onlineApplicationAllFieldsList,
// } from "@/src/constants/applicationOnlineConsts";

interface ColumnConfig {
  title: string;
  id: string;
  width?: number;
}

interface ApplicationsTableState {
  data: IADatabaseDocument[];
  visibleColumns: ColumnConfig[];
  filterAndSearch: IADatabaseSearchAndFilters;
  hiddenColumns: ColumnConfig[];
  selectedRows: number[];
}

const userConfig = getUserConfigFromStorage();
const initialVisibleColumns = normalizeVisibleAODColumns(
  userConfig?.AODPConfig?.AODPTable || admissionDatabaseDefaultConfiguration,
);

const initialState: ApplicationsTableState = {
  data: [],
  visibleColumns: initialVisibleColumns,
  filterAndSearch: admissionDatabaseDefaultBodyFilters,
  hiddenColumns: admissionDatabaseAllFieldsList.filter(
    (col) => !initialVisibleColumns.some((visibleCol) => visibleCol.id === col.id),
  ),
  selectedRows: [],
};

const AODatabaseTableSlice = createSlice({
  name: "AODatabaseTableSlice",
  initialState,
  reducers: {
    setData(state, action: PayloadAction<IADatabaseDocument[]>) {
      state.data = action.payload;
    },
    setFilterAndSearch(
      state,
      action: PayloadAction<{ key: string; value: string[] | number | string | null } | Record<string, string[] | number | string | null>>
    ) {
      if ("key" in action.payload) {
        // старый формат: { key, value }
        const { key, value } = action.payload;
        if (typeof key === "string") {
          // гарантируем что key — строка
          state.filterAndSearch = {
            ...state.filterAndSearch,
            [key]: value,
          };
        }
      } else {
        // новый формат: { start: value, end: value }
        state.filterAndSearch = {
          ...state.filterAndSearch,
          ...action.payload,
        };
      }
    },
    toggleColumnVisibility(state, action: PayloadAction<string>) {
      const colId = action.payload;
      const isVisible = state.visibleColumns.some((col) => col.id === colId);

      if (isVisible) {
        const col = state.visibleColumns.find((col) => col.id === colId)!;
        state.visibleColumns = state.visibleColumns.filter((col) => col.id !== colId);
        state.hiddenColumns.push({ title: col.title, id: col.id, width: col.width });
      } else {
        let col = state.hiddenColumns.find((col) => col.id === colId);
        if (!col) {
          col = admissionDatabaseAllFieldsList.find((c) => c.id === colId)!;
        }
        state.hiddenColumns = state.hiddenColumns.filter((c) => c.id !== colId);
        state.visibleColumns.push({ title: col.title, id: col.id, width: col.width ?? 4 });
      }
    },
    setColumnWidth(state, action: PayloadAction<{ id: string; width: number }>) {
      const { id, width } = action.payload;
      const col = state.visibleColumns.find((c) => c.id === id);
      if (col) col.width = width;
    },
    toggleRowSelection(state, action: PayloadAction<number>) {
      const id = action.payload;
      if (state.selectedRows.includes(id)) {
        state.selectedRows = state.selectedRows.filter((rowId) => rowId !== id);
      } else {
        state.selectedRows.push(id);
      }
    },
    toggleAllRows(state) {
      const rowIds = state.data.map((row) => row.id).filter((id): id is number => id != null);
      if (state.selectedRows.length === rowIds.length && rowIds.length > 0) {
        state.selectedRows = [];
      } else {
        state.selectedRows = rowIds;
      }
    },
    clearSelectedRows(state) {
      state.selectedRows = [];
    },
    setRowFieldValue(
      state,
      action: PayloadAction<{ id: number; field: keyof IADatabaseDocument; value: IADatabaseDocument[keyof IADatabaseDocument] }>,
    ) {
      const row = state.data.find((item) => item.id === action.payload.id);
      if (row) {
        (row as Record<string, unknown>)[action.payload.field] = action.payload.value;
      }
    },
    showAllColumns(state) {
      state.visibleColumns = [...admissionDatabaseAllFieldsList];
      state.hiddenColumns = [];
    },
    hideAllColumns(state) {
      state.hiddenColumns = [...admissionDatabaseAllFieldsList];
      state.visibleColumns = [];
    },
    setVisibleColumns(state, action: PayloadAction<ColumnConfig[]>) {
      state.visibleColumns = normalizeVisibleAODColumns(action.payload);
      // Обновляем hiddenColumns на основе видимых колонок
      state.hiddenColumns = admissionDatabaseAllFieldsList.filter(
        (col) => !state.visibleColumns.some((visibleCol) => visibleCol.id === col.id)
      );
    },
  },
});

export const {
  setData,
  setFilterAndSearch,
  toggleColumnVisibility,
  setColumnWidth,
  toggleRowSelection,
  toggleAllRows,
  clearSelectedRows,
  setRowFieldValue,
  showAllColumns,
  hideAllColumns,
  setVisibleColumns,
} = AODatabaseTableSlice.actions;

export default AODatabaseTableSlice.reducer;
